import {ChangeEvent, KeyboardEvent, MouseEvent as ReactMouseEvent, ReactElement, useEffect, useMemo, useRef, useState} from "react";
import {Textarea} from "@zendeskgarden/react-forms";
import * as css from "./styles/AutocompleteTextarea.module.scss";

export interface Keyword {
    keyword: string;
    details?: string;
}

export interface AutocompleteTextareaProps {
    keywords: Keyword[];
    value: string;
    onChange: (evt: React.ChangeEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
    rows?: number;
    style?: React.CSSProperties;
}

interface WordAtCaret {
    word: string;
    start: number;
    end: number;
}

function getWordAtCaret(text: string, caretPos: number): WordAtCaret | null {
    if (text.length === 0) {
        return null;
    }

    let start = caretPos;
    let end = caretPos;

    while (start > 0 && /["\w]/.test(text[start - 1]!)) {
        start--;
    }
    while (end < text.length && /["\w]/.test(text[end]!)) {
        end++;
    }

    const word = text.slice(start, end);
    if (word.length === 0) {
        return null;
    }

    return {word, start, end};
}

export function AutocompleteTextarea(props: AutocompleteTextareaProps): ReactElement {
    const {keywords, value, onChange, placeholder, rows, style} = props;
    const [suggestions, setSuggestions] = useState<Keyword[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [wordInfo, setWordInfo] = useState<WordAtCaret | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const lowerKeywords = useMemo<[string, Keyword][]>(() => {
        return keywords.map(kw => [kw.keyword.toLowerCase(), kw]);
    }, [keywords]);

    const updateSuggestions = (el: HTMLTextAreaElement) => {
        const caretPos = el.selectionStart;
        const info = getWordAtCaret(el.value, caretPos);
        setWordInfo(info);

        if (info && info.word.length > 0) {
            const lower = info.word.toLowerCase();
            const matches = lowerKeywords.filter(val => val[0].startsWith(lower) && val[0] !== lower);
            setSuggestions(matches.map(val => val[1]));
            setSelectedIndex(0);
        } else {
            setSuggestions([]);
        }
    };

    const applySuggestion = (keyword: Keyword) => {
        if (wordInfo == null || textareaRef.current == null) {
            return;
        }

        const before = value.slice(0, wordInfo.start);
        const after = value.slice(wordInfo.end);
        const newValue = before + keyword.keyword + after;
        const newCaretPos = wordInfo.start + keyword.keyword.length;

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLTextAreaElement.prototype, "value"
        )?.set;
        if (nativeInputValueSetter) {
            nativeInputValueSetter.call(textareaRef.current, newValue);
            const event = new Event("change", {bubbles: true});
            textareaRef.current.dispatchEvent(event);
        }

        setSuggestions([]);
        setWordInfo(null);

        requestAnimationFrame(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(newCaretPos, newCaretPos);
            }
        });
    };

    function handleChange(evt: ChangeEvent<HTMLTextAreaElement>) {
        onChange(evt);
        updateSuggestions(evt.target);
    }

    function handleKeyUp(evt: KeyboardEvent<HTMLTextAreaElement>) {
        if (evt.key === "ArrowLeft" || evt.key === "ArrowRight" || evt.key === "Home" || evt.key === "End") {
            updateSuggestions(evt.currentTarget);
        }
    }

    function handleClick(evt: ReactMouseEvent<HTMLTextAreaElement>) {
        updateSuggestions(evt.currentTarget);
    }

    function handleKeyDown(evt: KeyboardEvent<HTMLTextAreaElement>) {
        if (suggestions.length === 0) {
            return;
        }

        if (evt.key === "ArrowDown") {
            evt.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        } else if (evt.key === "ArrowUp") {
            evt.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, 0));
        } else if (evt.key === "Tab" || evt.key === "Enter") {
            evt.preventDefault();
            applySuggestion(suggestions[selectedIndex]!);
        } else if (evt.key === "Escape") {
            setSuggestions([]);
        }
    }

    useEffect(() => {
        function handleClickOutside(evt: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(evt.target as Node)) {
                setSuggestions([]);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={containerRef} className={css.container}>
            <Textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                onClick={handleClick}
                placeholder={placeholder}
                rows={rows}
                style={style}
            />
            {suggestions.length > 0 &&
                <ul className={css.suggestions}>
                    {suggestions.map((keyword, idx) => (
                        <li
                            key={keyword.keyword}
                            className={idx === selectedIndex ? css.selected : undefined}
                            onMouseDown={(evt) => {
                                evt.preventDefault();
                                applySuggestion(keyword);
                            }}
                            onMouseEnter={() => setSelectedIndex(idx)}
                        >
                            <code>{keyword.keyword}</code>
                            {keyword.details && <div className={css.details}>{keyword.details}</div>}
                        </li>
                    ))}
                </ul>
            }
        </div>
    );
}
